import { HotFaceAuth } from '@hotmart-org-ca/hot-face-auth';

import type { FaceTecIDScanResult, FaceTecSessionResult } from '@/common/contexts/types';
import { ONBOARDING_SESSION_KEY } from '@/common/constants';
import { checkFaceTecConfiguration } from './facetec-config-checker';

/**
 * Callback function type for biometric operation results
 */
export type BiometricCallback = (result: FaceTecSessionResult | FaceTecIDScanResult) => void;

/**
 * SDK status and diagnostic information
 */
export interface SDKStatus {
  isCompletelyDone?: boolean;
  status?: string;
  details?: {
    browserSupported?: boolean;
    browserInfo?: {
      reason?: string;
    };
    environmentValid?: boolean;
    environmentErrors?: string[];
    sdkLoaded?: boolean;
    sdkStatusCode?: number;
  };
}

/**
 * FaceTecSingleton state interface
 */
export interface FaceTecSingletonState {
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;
  sdkStatus: SDKStatus | null;
  initializationAttempts: number;
  lastFailureReason: string | null;
  hasPermanentFailure: boolean;
}

/**
 * Singleton class for managing FaceTec SDK instance and operations.
 *
 * This class provides:
 * - Single instance management to prevent multiple SDK initializations
 * - Automatic initialization and retry logic
 * - Callback management for multiple consumers
 * - Comprehensive error handling and diagnostics
 * - Proper cleanup and deinitialization
 *
 * @example
 * ```typescript
 * // Initialize SDK
 * const initialized = await FaceTecSingleton.ensureInitialized(getDiagnosticMessage);
 *
 * if (initialized) {
 *   // Add callback for results
 *   FaceTecSingleton.addCallback((result) => {
 *     console.log('Biometric result:', result);
 *   });
 *
 *   // Get instance and perform operations
 *   const instance = await FaceTecSingleton.getInstance();
 *   await instance?.doEnrollment();
 * }
 * ```
 */
export class FaceTecSingleton {
  private static instance: HotFaceAuth | null = null;
  private static isInitializing = false;
  private static isInitialized = false;
  private static callbacks: Set<BiometricCallback> = new Set();
  private static initializationPromise: Promise<boolean> | null = null;
  private static error: string | null = null;
  private static sdkStatus: SDKStatus | null = null;
  private static initializationAttempts = 0;
  private static lastFailureReason: string | null = null;
  private static hasPermanentFailure = false;

  /**
   * Get the current FaceTec SDK instance if initialized
   * @returns Promise resolving to HotFaceAuth instance or null
   */
  static async getInstance(): Promise<HotFaceAuth | null> {
    if (this.instance && this.isInitialized) {
      return this.instance;
    }

    if (this.isInitializing && this.initializationPromise) {
      await this.initializationPromise;
      return this.instance;
    }

    return null;
  }

  /**
   * Ensure the SDK is initialized and ready for use
   * @param getDiagnosticMessage Function to generate diagnostic messages from SDK status
   * @returns Promise resolving to true if initialization succeeded
   */
  static async ensureInitialized(
    getDiagnosticMessage: (status: SDKStatus) => string
  ): Promise<boolean> {
    // If already initialized, verify instance is still valid
    if (this.instance && this.isInitialized) {
      try {
        // Check if SDK is still active by consulting status
        const status = this.instance.getSDKStatus ? this.instance.getSDKStatus() : null;
        if (status && status.isCompletelyDone !== false) {
          console.log('Using existing initialized FaceTec instance');
          return true;
        }
      } catch {
        console.warn('FaceTec instance is no longer valid, reinitializing...');
        this.isInitialized = false;
        this.instance = null;
      }
    }

    if (this.isInitializing && this.initializationPromise) {
      console.log('Waiting for initialization in progress...');
      return await this.initializationPromise;
    }

    this.initializationPromise = this.initializeSDK(getDiagnosticMessage);
    return await this.initializationPromise;
  }

  /**
   * Initialize the FaceTec SDK
   * @private
   */
  private static async initializeSDK(
    getDiagnosticMessage: (status: SDKStatus) => string
  ): Promise<boolean> {
    this.isInitializing = true;
    this.error = null;
    this.initializationAttempts++;

    console.log(`Initializing FaceTec SDK (attempt ${this.initializationAttempts})...`);

    try {
      const configCheck = checkFaceTecConfiguration();
      if (!configCheck.isValid) {
        console.error('FaceTec Configuration Issues:', configCheck.issues);

        const criticalIssues = configCheck.issues.filter(
          (issue) =>
            issue.includes('camera') || issue.includes('WebAssembly') || issue.includes('Storage')
        );

        if (criticalIssues.length > 0) {
          const criticalError = `Critical configuration issues: ${criticalIssues.join(', ')}`;
          this.lastFailureReason = criticalError;
          this.error = criticalError;
          this.hasPermanentFailure = true;
          throw new Error(criticalError);
        }
      }

      const sessionKey = sessionStorage.getItem(ONBOARDING_SESSION_KEY);
      if (!sessionKey) {
        throw new Error('No session key found. Please refresh the page and try again.');
      }

      // Clean up existing instance first
      if (this.instance) {
        console.log('Cleaning up previous instance...');
        if (this.instance.cleanup) {
          this.instance.cleanup();
        }
        this.instance = null;
      }

      // Create new instance
      this.instance = new HotFaceAuth(sessionKey, 'id_verify', 'register_face_id_and_ocr', true);

      const initialized = await this.instance.ensureInitialized();

      if (initialized) {
        this.sdkStatus = this.instance.getSDKStatus ? this.instance.getSDKStatus() : null;
        this.isInitialized = true;
        this.error = null;
        this.lastFailureReason = null;

        console.log('FaceTec SDK initialized successfully (singleton)');

        // Configure callbacks for all registered instances
        this.setupCallbacks();

        return true;
      } else {
        this.sdkStatus = this.instance.getSDKStatus ? this.instance.getSDKStatus() : null;
        const failureReason = getDiagnosticMessage(this.sdkStatus || {});
        this.lastFailureReason = failureReason;
        this.error = `SDK initialization failed: ${failureReason}`;

        throw new Error(this.error);
      }
    } catch (err) {
      console.error('FaceAuth singleton initialization failed:', err);
      this.error = err instanceof Error ? err.message : 'Initialization failed';

      if (this.initializationAttempts >= 3) {
        this.hasPermanentFailure = true;
      }

      return false;
    } finally {
      this.isInitializing = false;
      setTimeout(() => {
        this.initializationPromise = null;
      }, 500);
    }
  }

  /**
   * Set up callbacks for all registered consumers
   * @private
   */
  private static setupCallbacks(): void {
    if (this.instance && this.callbacks.size > 0) {
      this.instance.setExternalCallback(
        (sessionResult: FaceTecSessionResult, idScanResult: FaceTecIDScanResult) => {
          // Call all registered callbacks
          this.callbacks.forEach((cb) => {
            try {
              if (sessionResult) cb(sessionResult);
              if (idScanResult) cb(idScanResult);
            } catch {
              console.error('Error executing callback');
            }
          });
        }
      );
    }
  }

  /**
   * Add a callback to receive biometric operation results
   * @param callback Function to call with results
   */
  static addCallback(callback: BiometricCallback): void {
    this.callbacks.add(callback);

    // If SDK is already initialized, set up callbacks immediately
    if (this.instance && this.isInitialized) {
      this.setupCallbacks();
    }
  }

  /**
   * Remove a callback from receiving biometric operation results
   * @param callback Function to remove
   */
  static removeCallback(callback: BiometricCallback): void {
    this.callbacks.delete(callback);
  }

  /**
   * Get current state of the singleton
   * @returns Current state object
   */
  static getState(): FaceTecSingletonState {
    return {
      isInitialized: this.isInitialized,
      isInitializing: this.isInitializing,
      error: this.error,
      sdkStatus: this.sdkStatus,
      initializationAttempts: this.initializationAttempts,
      lastFailureReason: this.lastFailureReason,
      hasPermanentFailure: this.hasPermanentFailure,
    };
  }

  /**
   * Retry SDK initialization after failure
   * @param getDiagnosticMessage Function to generate diagnostic messages
   * @returns Promise resolving to true if retry succeeded
   */
  static async retry(getDiagnosticMessage: (status: SDKStatus) => string): Promise<boolean> {
    console.log('Restarting FaceTec SDK...');

    // First, completely deinitialize the SDK
    await this.deinitialize();

    // Reset counters and permanent failure flag
    this.initializationAttempts = 0;
    this.error = null;
    this.lastFailureReason = null;
    this.hasPermanentFailure = false;

    // Small delay before retry
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Reinitialize from scratch
    return await this.ensureInitialized(getDiagnosticMessage);
  }

  /**
   * Clean up the singleton instance
   */
  static cleanup(): void {
    if (this.instance && this.instance.cleanup) {
      this.instance.cleanup();
    }
    this.instance = null;
    this.isInitialized = false;
    this.isInitializing = false;
    this.callbacks.clear();
    this.initializationPromise = null;
    this.hasPermanentFailure = false;
  }

  /**
   * Deinitialize the SDK and clean up resources
   * @returns Promise that resolves when deinitialization is complete
   */
  static deinitialize(): Promise<void> {
    return new Promise((resolve) => {
      try {
        // Use FaceTecSDK.deinitialize if available
        if (typeof window !== 'undefined' && window.FaceTecSDK && window.FaceTecSDK.deinitialize) {
          window.FaceTecSDK.deinitialize(() => {
            console.log('FaceTec SDK deinitialized');
            this.cleanup();
            resolve();
          });
        } else {
          // Fallback to manual cleanup
          this.cleanup();
          resolve();
        }
      } catch {
        console.error('Error during deinitialization');
        this.cleanup();
        resolve();
      }
    });
  }
}
